<template>
  <RfSelectFieldShell
    ref="shellEl"
    :title="title"
    :preset="preset"
    :requiredTitle="requiredTitle"
    :variant="variant"
    :size="size"
    :autoWidth="autoWidth"
    :errorMessage="errorMessage"
    :hintMessage="hintMessage"
    :clearable="clearable"
    :hasValue="hasSelection"
    :disabled="disabled"
    :loading="loading"
    :hideIcon="hideIcon"
    :sameWidth="sameWidth"
    :menuPlacement="menuPlacement"
    @clear="clearSelection"
    @show="onShow"
    @hide="onHide"
  >
    <template v-if="slots.title" #title>
      <slot name="title" />
    </template>

    <slot v-if="slots.activator" name="activator" :opened="!!shellEl?.opened" />
    <template v-else-if="loading">
      <span class="text-beige-40 block truncate text-start">{{ loadingText }}</span>
    </template>
    <template v-else-if="extended">
      <span v-if="activator" class="inline-flex h-6 items-center gap-1">
        <RfFilterAlterIcon v-if="hideIcon" class="h-6 w-6" />
        <span class="text-body text-black-1">{{ activator }}</span>
      </span>
      <span class="text-beige-50 inline-flex items-center" :class="disabled ? 'pr-2' : 'pr-1'">
        <template v-if="selectedValuesAll.length === 0">
          {{ items.length ? "All" : placeholder }}
        </template>
        <span v-else class="text-beige-90 inline-flex items-center">
          <RfColorTag
            v-for="(value, index) in selectedValuesAll.slice(0, visibleCount)"
            :key="index"
            size="small"
            pill
            backgroundColor="#EDEBE4"
            disabled
          >
            {{ value }}
          </RfColorTag>
          <RfColorTag
            v-if="selectedValuesAll.length > visibleCount"
            size="small"
            pill
            backgroundColor="#EDEBE4"
            disabled
          >
            +{{ selectedValuesAll.length - visibleCount }}
          </RfColorTag>
        </span>
      </span>
    </template>
    <template v-else-if="multiple">
      <slot v-if="slots.selected && hasSelection" name="selected" :props="input"></slot>
      <span v-else-if="hasSelection" class="block truncate text-start">
        {{ selectedLabels }}
      </span>
      <span v-else class="text-beige-40 block truncate text-start">{{ placeholder }}</span>
    </template>
    <template v-else>
      <slot v-if="slots.selected && hasSelection" name="selected" :props="entry"></slot>
      <span v-else-if="hasSelection" class="block truncate text-start">
        {{ entry[itemValue] || entry }}
      </span>
      <span v-else class="text-beige-40 block truncate text-start">{{ placeholder }}</span>
    </template>

    <template #menu>
      <div v-if="search" class="w-full px-3 pt-3">
        <input
          ref="searchInputEl"
          v-model="searchValue"
          :aria-label="typeof search === 'string' ? search : 'Search'"
          :placeholder="typeof search === 'string' ? search : 'Search...'"
          class="bg-goal w-full rounded p-3 disabled:opacity-50"
          @keydown.down.prevent="focusFirstOption"
        />
      </div>

      <div class="flex w-full min-w-px grow flex-col overflow-y-auto p-4 px-2">
        <template v-for="(item, index) in filteredItems" :key="itemRowKey(item)">
          <RfButtonNew
            v-if="!multiple"
            :ref="el => setOptionRef(el, index)"
            type="none"
            size="none"
            role="option"
            :aria-selected="isItemSelected(item)"
            :disabled="itemDisabled(item) ? itemDisabledMessage(item) || true : false"
            class="hover:bg-yellow/10 focus-visible:bg-yellow/10 w-full shrink-0 rounded px-4 py-2 text-start text-base [&_.rf-button--container]:w-full [&_.rf-button--container]:justify-start [&.disabled]:cursor-not-allowed! [&.disabled]:opacity-50!"
            :class="{ 'bg-yellow!': isItemSelected(item) }"
            @click="() => selectItem(item)"
            @keydown="event => onOptionKeydown(event, index)"
          >
            <slot v-if="slots.item" name="item" :props="item" :item="item" :index="index"></slot>
            <template v-else>{{ item[itemValue] ?? item }}</template>
          </RfButtonNew>
          <div
            v-else
            :ref="el => setOptionRef(el, index)"
            role="option"
            tabindex="0"
            :aria-selected="isItemSelected(item)"
            class="hover:bg-yellow/10 focus-visible:bg-yellow/10 flex w-full shrink-0 items-center gap-2 rounded px-2 py-1 outline-none"
            @keydown="event => onOptionKeydown(event, index)"
          >
            <RfCheckbox
              :modelValue="item.isCustom ? true : isItemSelected(item)"
              size="small"
              class="rf-row--checkbox w-full text-sm!"
              :disabled="item.isCustom || itemDisabled(item)"
              @update:modelValue="
                () => (item.isCustom ? removeCustomValue(item) : selectItem(item))
              "
            >
              <slot v-if="slots.item" name="item" :item="item" :index="index"></slot>
              <template v-else>{{ item[itemValue] ?? item }}</template>
            </RfCheckbox>
            <button
              v-if="item.isCustom"
              class="shrink-0"
              aria-label="Remove custom value"
              @click="removeCustomValue(item)"
            >
              <RfDeleteIcon class="fill-vermillion-50 h-5! w-5!" />
            </button>
          </div>
        </template>
        <span v-if="!filteredItems.length" class="text-grey-3 px-2 py-2 text-sm"
          >No results found</span
        >

        <div v-if="customValues && multiple" class="w-full px-2 pt-2">
          <RfCheckbox
            class="rf-row--checkbox rf-row--checkbox-col-selected w-full text-sm!"
            size="small"
            :modelValue="true"
            disabled
          >
            <span class="inline-flex w-full items-center gap-2">
              <input
                v-model="newCustomValue"
                class="bg-goal w-full rounded p-2"
                aria-label="Enter custom value"
                placeholder="Enter custom value"
                @keydown.enter.prevent="addCustomValue(newCustomValue)"
              />
              <button
                class="inline-flex items-center"
                aria-label="Add custom value"
                @click="addCustomValue(newCustomValue)"
              >
                <RfAddIcon class="fill-beige-50 h-6 w-6" />
              </button>
            </span>
          </RfCheckbox>
          <span v-if="customValueError" class="text-error-1 mt-1 block text-xs">
            {{ customValueError }}
          </span>
        </div>
      </div>

      <div
        v-if="multiple && ((search && localSelectedCount > 0) || apply)"
        class="mb-2 flex w-full min-w-36"
      >
        <span v-if="search && localSelectedCount > 0" class="text-grey-3 pt-2 pl-4">
          {{ localSelectedCount }} selected
          <span v-if="searchedLocalSelectedCount !== localSelectedCount">
            ({{ localSelectedCount - searchedLocalSelectedCount }} hidden)
          </span>
        </span>

        <RfButtonNew
          v-if="apply"
          class="mr-4 ml-auto pr-2! pl-5! [&_.rf-button--container]:font-semibold!"
          size="link-sm"
          type="link"
          data-cy="table-filter--apply"
          @click="applySelection"
        >
          Apply
        </RfButtonNew>
      </div>
    </template>
  </RfSelectFieldShell>
</template>

<script>
const defaultMap = { key: "value", value: "text" };
</script>

<script setup>
import { ref, useSlots, computed, nextTick, watch } from "vue";
import RfAddIcon from "@/components/icons/RfAddIcon.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfFilterAlterIcon from "@/components/icons/RfFilterAlterIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfSelectFieldShell from "@/components/inputs/RfSelectFieldShell.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";

const slots = useSlots();
const emit = defineEmits(["show", "hide", "selected", "update:hasError", "removeCustomValue"]);

const input = defineModel("modelValue", { type: [String, Number, Array, Object], default: "" });

const props = defineProps({
  preset: { type: String, default: null },
  placeholder: { type: String, default: "Select an option" },
  title: { type: String, default: "" },
  size: { type: String, default: "base" },
  variant: { type: String, default: "default" },
  autoWidth: { type: Boolean, default: false },
  sameWidth: { type: Boolean, default: false },
  // floating-ui placement for the dropdown menu; null = preset/global default
  menuPlacement: { type: String, default: null },
  requiredTitle: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  hintMessage: { type: String, default: "" },
  disabled: { type: [Boolean, String], default: false },
  loading: { type: Boolean, default: false },
  loadingText: { type: String, default: "Loading..." },
  clearable: { type: Boolean, default: false },
  hideIcon: { type: Boolean, default: false },
  // "Filter badge" trigger look (activator label + selected-count badge) — see RfFilter.
  extended: { type: Boolean, default: false },
  visibleCount: { type: [Number, String], default: 1 },
  activator: { type: String, default: "" },

  items: { type: Array, default: () => [] },
  itemKey: { type: String, default: defaultMap.key },
  itemValue: { type: String, default: defaultMap.value },
  returnObject: { type: Boolean, default: false },
  itemDisabled: { type: Function, default: () => false },
  itemDisabledMessage: { type: Function, default: () => "" },
  multiple: { type: Boolean, default: false },
  mandatory: { type: Boolean, default: false },
  apply: { type: Boolean, default: false },
  customValues: { type: Boolean, default: false },
  customValueRules: { type: Array, default: () => [] },
  // Values to pre-seed as custom (disabled checkbox + trash button) rather than as regular,
  // non-removable `items` — e.g. free-text values a user has typed in before, as opposed to
  // a fixed catalog of options.
  customValueSeeds: { type: Array, default: () => [] },
  // "a, b, c" adds all three at once when typed/pasted into the custom-value input.
  customValueDelimiter: { type: String, default: "," },

  search: { type: [Boolean, String], default: false },
});

const shellEl = ref();
const searchValue = ref("");
const searchInputEl = ref();
const optionRefs = ref([]);
const draftSelection = ref([]);
// Custom values: pre-seeded via `customValueSeeds`, plus whatever gets added through the
// "Enter custom value" row during this session.
const extraItems = ref(
  props.customValueSeeds.map(v => ({ [props.itemKey]: v, [props.itemValue]: v })),
);
watch(
  () => props.customValueSeeds,
  seeds => {
    extraItems.value = seeds.map(v => ({ [props.itemKey]: v, [props.itemValue]: v }));
  },
);
const newCustomValue = ref("");
const customValueError = ref(null);

const isTriggerDisabled = computed(() => props.disabled || props.loading);
const visibleCount = computed(() => Number(props.visibleCount));

const resetDraft = () => {
  draftSelection.value = Array.isArray(input.value) ? [...input.value] : [];
};
resetDraft();

const onShow = () => {
  if (props.apply) resetDraft();
  focusMenu();
  emit("show");
};
const onHide = () => {
  searchValue.value = "";
  discardPendingCustomValue();
  emit("hide");
};

const hasSelection = computed(() => {
  const v = input.value;
  if (props.multiple) return Array.isArray(v) && v.length > 0;
  if (v == null || v === "") return false;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
});

const allItems = computed(() =>
  props.customValues
    ? [...props.items, ...extraItems.value.map(i => ({ ...i, isCustom: true }))]
    : props.items,
);

const filteredItems = computed(() => {
  if (!props.search || !searchValue.value) return allItems.value;
  const query = searchValue.value.toLowerCase();
  return allItems.value.filter(i => {
    const text = typeof i === "object" ? i[props.itemValue] : i;
    return text?.toLowerCase?.().includes(query);
  });
});

const entry = computed(
  () =>
    props.items.find(i => {
      if (typeof i === "object") {
        return props.returnObject
          ? input.value != null && i[props.itemKey] === input.value[props.itemKey]
          : i[props.itemKey] === input.value;
      }
      return i === input.value;
    }) ??
    input.value ??
    "",
);

const isItemSelected = item => {
  const key = item[props.itemKey] ?? item;
  if (props.multiple) {
    const selection = props.apply ? draftSelection.value : input.value || [];
    return selection.some(v => (props.returnObject ? v[props.itemKey] === key : v === key));
  }
  return key === (entry.value[props.itemKey] ?? entry.value);
};

const selectItem = item => {
  if (isTriggerDisabled.value || item.isCustom || props.itemDisabled(item)) return;
  if (props.multiple) {
    const val = (props.returnObject ? item : item[props.itemKey]) ?? item;
    const key = item[props.itemKey] ?? item;
    const base = props.apply ? draftSelection.value : input.value;
    const arr = Array.isArray(base) ? [...base] : [];
    const idx = arr.findIndex(v => (props.returnObject ? v[props.itemKey] === key : v === key));
    if (idx >= 0) {
      if (props.mandatory && arr.length === 1) return;
      arr.splice(idx, 1);
    } else arr.push(val);
    if (props.apply) draftSelection.value = arr;
    else input.value = arr;
  } else {
    input.value = (props.returnObject ? item : item[props.itemKey]) ?? item;
    shellEl.value?.hide?.();
  }
};

const clearSelection = () => {
  if (props.mandatory) return;
  input.value = props.multiple ? [] : "";
};

const selectedLabels = computed(() =>
  (input.value || [])
    .map(v => {
      const item = allItems.value.find(i =>
        props.returnObject ? i[props.itemKey] === v[props.itemKey] : (i[props.itemKey] ?? i) === v,
      );
      return item ? (item[props.itemValue] ?? item) : v;
    })
    .join(", "),
);

const selectedValuesAll = computed(() => {
  if (!props.multiple)
    return hasSelection.value ? [entry.value[props.itemValue] ?? entry.value] : [];
  return (input.value || []).map(v => {
    const item = props.items.find(i =>
      props.returnObject ? i[props.itemKey] === v[props.itemKey] : (i[props.itemKey] ?? i) === v,
    );
    if (item) return item[props.itemValue] ?? item;
    return typeof v === "object" ? v[props.itemValue] : v;
  });
});

const currentSelection = computed(() =>
  props.apply ? draftSelection.value : Array.isArray(input.value) ? input.value : [],
);

const localSelectedCount = computed(() => currentSelection.value.length);

const searchedLocalSelectedCount = computed(() => {
  if (!props.search || !searchValue.value) return localSelectedCount.value;
  const query = searchValue.value.toLowerCase();
  return currentSelection.value.filter(v => {
    const key = typeof v === "object" ? v[props.itemKey] : v;
    const item = allItems.value.find(i => (typeof i === "object" ? i[props.itemKey] : i) === key);
    const text = item
      ? (item[props.itemValue] ?? item)
      : typeof v === "object"
        ? v[props.itemValue]
        : v;
    return text?.toLowerCase?.().includes(query);
  }).length;
});

const itemRowKey = item => item[props.itemKey] ?? item;

const setOptionRef = (el, index) => {
  optionRefs.value[index] = el;
};
const focusOption = index => {
  const el = optionRefs.value[index];
  (el?.$el || el)?.focus?.();
};
const findFocusableIndex = (startIndex, step) => {
  const list = filteredItems.value;
  for (let i = startIndex; i >= 0 && i < list.length; i += step) {
    if (!list[i].isCustom && !props.itemDisabled(list[i])) return i;
  }
  return -1;
};
const focusFirstOption = () => {
  const target = findFocusableIndex(0, 1);
  if (target >= 0) focusOption(target);
};
const focusMenu = () => {
  nextTick(() => {
    if (props.search) {
      searchInputEl.value?.focus();
      return;
    }
    const selectedIndex = filteredItems.value.findIndex(item => isItemSelected(item));
    const target = findFocusableIndex(selectedIndex >= 0 ? selectedIndex : 0, 1);
    if (target >= 0) focusOption(target);
  });
};
const onOptionKeydown = (event, index) => {
  let target;
  switch (event.key) {
    case "ArrowDown":
      target = findFocusableIndex(index + 1, 1);
      break;
    case "ArrowUp":
      target = findFocusableIndex(index - 1, -1);
      break;
    case "Home":
      target = findFocusableIndex(0, 1);
      break;
    case "End":
      target = findFocusableIndex(filteredItems.value.length - 1, -1);
      break;
    default:
      return;
  }
  event.preventDefault();
  if (target >= 0) focusOption(target);
};

const runCustomValueRules = value => {
  for (const rule of props.customValueRules) {
    const result = rule(value);
    if (result !== true) return result;
  }
  return null;
};

// Splits on `customValueDelimiter` so pasting/typing "a, b, c" adds all three at once —
// falls back to treating the whole string as one value when the delimiter isn't present.
const addCustomValue = valueIn => {
  const parts = (valueIn ?? "")
    .split(props.customValueDelimiter)
    .map(v => v.trim())
    .filter(Boolean);
  if (!parts.length) return;

  for (const part of parts) {
    const error = runCustomValueRules(part);
    if (error) {
      customValueError.value = error;
      emit("update:hasError", true);
      return;
    }
  }
  customValueError.value = null;
  emit("update:hasError", false);

  const newExtra = parts.filter(p => !extraItems.value.some(i => i[props.itemKey] === p));
  if (newExtra.length) {
    extraItems.value = [
      ...extraItems.value,
      ...newExtra.map(p => ({ [props.itemKey]: p, [props.itemValue]: p })),
    ];
  }

  const toVal = p => (props.returnObject ? { [props.itemKey]: p, [props.itemValue]: p } : p);
  const notSelected = (list, p) =>
    !list.some(v => (props.returnObject ? v[props.itemKey] === p : v === p));
  if (props.apply) {
    const additions = parts.filter(p => notSelected(draftSelection.value, p)).map(toVal);
    if (additions.length) draftSelection.value = [...draftSelection.value, ...additions];
  } else {
    const arr = Array.isArray(input.value) ? [...input.value] : [];
    const additions = parts.filter(p => notSelected(arr, p)).map(toVal);
    if (additions.length) input.value = [...arr, ...additions];
  }
  newCustomValue.value = "";
  searchValue.value = "";
};

const removeCustomValue = item => {
  const key = item[props.itemKey];
  extraItems.value = extraItems.value.filter(i => i[props.itemKey] !== key);
  const matches = v => (props.returnObject ? v[props.itemKey] === key : v === key);
  if (props.apply) draftSelection.value = draftSelection.value.filter(v => !matches(v));
  else if (Array.isArray(input.value)) input.value = input.value.filter(v => !matches(v));
  emit("removeCustomValue", key);
};

const discardPendingCustomValue = () => {
  newCustomValue.value = "";
  customValueError.value = null;
  emit("update:hasError", false);
};

const applySelection = () => {
  emit("selected", draftSelection.value);
  shellEl.value?.hide?.();
};

defineExpose({ opened: computed(() => shellEl.value?.opened) });
</script>
